import { devLog, devError } from '../utils/devLog';
import { getDefaultNakshatraChakraVisibility, getDefaultNakshatraPadaVisibility } from '../config/nakshatraDefaults';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
/**
 * ChakraInitializationManager
 *
 * Singleton service to manage chakra initialization events and prevent duplicates.
 * Ensures each chart's chakra settings are emitted exactly once during initialization.
 */

export interface ChakraState {
    showMuhurtaChakra: boolean;
    showAshtamshaChakra: boolean;
    showNakshatraChakra: boolean;
    showNakshatraPada: boolean;
}

export interface InitializationEvent {
    id: string;
    type: 'muhurta.sectors.toggle' | 'ashtamsha.sectors.toggle' | 'nakshatra.chakra.toggle' | 'nakshatra.pada.toggle';
    source: string;
    timestamp: number;
    chartId: string;
    data: {
        chartId: string;
        showMuhurtaSectors?: boolean;
        showAshtamshaSectors?: boolean;
        showNAKSHATRAChakra?: boolean;
        showPada?: boolean;
    };
}

class ChakraInitializationManager {
    private static instance: ChakraInitializationManager;
    private initializedCharts: Set<string> = new Set();

    private constructor() {}

    public static getInstance(): ChakraInitializationManager {
        if (!ChakraInitializationManager.instance) {
            ChakraInitializationManager.instance = new ChakraInitializationManager();
        }
        return ChakraInitializationManager.instance;
    }

    /**
     * Initialize chakra events for a chart (called once per chart)
     */
    public initializeChakraEvents(chartId: string, source: string = 'chakra-manager'): boolean {
        // Check if already initialized
        if (this.initializedCharts.has(chartId)) {
            return false;
        }

        // Get current chart settings
        const chartSettings = this.getChartSettings(chartId);
        if (!chartSettings) {
            return false;
        }

        // Mark as initialized
        this.initializedCharts.add(chartId);

        // Non-chakra views take over the entire canvas — skip chakra initialization
        const rawSettings = window.multipleChartsContext?.charts?.get(chartId)?.settings;
        if (rawSettings?.activeView && rawSettings.activeView !== 'chakra') {
            return true;
        }

        let eventsEmitted = 0;

        // Emit muhurta event if enabled
        if (chartSettings.showMuhurtaChakra) {
            const muhurtaEvent: InitializationEvent = {
                id: `muhurta-init-${chartId}-${Date.now()}`,
                type: 'muhurta.sectors.toggle',
                source: source,
                timestamp: Date.now(),
                chartId,
                data: { chartId, showMuhurtaSectors: true }
            };
            globalEventStream.emit(muhurtaEvent);
            eventsEmitted++;
        }

        // Emit ashtamsha event if enabled
        if (chartSettings.showAshtamshaChakra) {
            const ashtamshaEvent: InitializationEvent = {
                id: `ashtamsha-init-${chartId}-${Date.now()}`,
                type: 'ashtamsha.sectors.toggle',
                source: source,
                timestamp: Date.now(),
                chartId,
                data: { chartId, showAshtamshaSectors: true }
            };
            globalEventStream.emit(ashtamshaEvent);
            eventsEmitted++;
        }

        // Emit nakshatra event if enabled
        if (chartSettings.showNakshatraChakra) {
            // Use nakshatra.chakra.toggle to match TabChartRenderer subscription
            const nakshatraEvent = {
                id: `nakshatra-init-${chartId}-${Date.now()}`,
                type: 'nakshatra.chakra.toggle',
                source: source,
                timestamp: Date.now(),
                chartId,
                data: { chartId, showNAKSHATRAChakra: true }
            };
            globalEventStream.emit(nakshatraEvent);
            eventsEmitted++;

            // Emit pada event AFTER nakshatra if both enabled (pada depends on nakshatra)
            if (chartSettings.showNakshatraPada) {
                const padaEvent: InitializationEvent = {
                    id: `pada-init-${chartId}-${Date.now()}`,
                    type: 'nakshatra.pada.toggle',
                    source: source,
                    timestamp: Date.now(),
                    chartId,
                    data: { chartId, showPada: true }
                };
                devLog(`📡 [ChakraInitializationManager] Emitting pada event:`, padaEvent);
                globalEventStream.emit(padaEvent);
                eventsEmitted++;
            } else {
                devLog(`⏭️ [ChakraInitializationManager] Skipping pada event (showNakshatraPada=${chartSettings.showNakshatraPada})`);
            }
        }

        return eventsEmitted > 0;
    }

    /**
     * Get chart settings from MultipleChartsContext
     */
    private getChartSettings(chartId: string): ChakraState | null {
        const multipleChartsContext = window.multipleChartsContext;
        if (!multipleChartsContext?.charts?.has(chartId)) {
            return null;
        }

        const chart = multipleChartsContext.charts.get(chartId);
        const settings = chart?.settings;

        return {
            showMuhurtaChakra: settings?.showMuhurtaChakra || false,
            showAshtamshaChakra: settings?.showAshtamshaChakra || false,
            showNakshatraChakra: settings?.showNakshatraChakra ?? getDefaultNakshatraChakraVisibility(),
            showNakshatraPada: settings?.showNakshatraPada ?? getDefaultNakshatraPadaVisibility()
        };
    }

    /**
     * Check if chart is already initialized
     */
    public isChartInitialized(chartId: string): boolean {
        return this.initializedCharts.has(chartId);
    }

    /**
     * Reset initialization state (for testing/debugging)
     */
    public resetChart(chartId: string): void {
        this.initializedCharts.delete(chartId);
    }

    /**
     * Reset all initialization states (for testing/debugging)
     */
    public resetAll(): void {
        this.initializedCharts.clear();
    }

    /**
     * Get initialization status for debugging
     */
    public getStatus(): { initializedCharts: string[] } {
        return {
            initializedCharts: Array.from(this.initializedCharts)
        };
    }
}

// Export singleton instance
export const chakraInitializationManager = ChakraInitializationManager.getInstance();

// Make available globally for debugging
if (typeof window !== 'undefined') {
    window.chakraInitializationManager = chakraInitializationManager;
}

export default chakraInitializationManager;